import React, {useEffect, useState} from 'react';
import {Download, FileText, AlertCircle} from 'lucide-react';
import pngIcon from "../../assets/types/png.png";
import pdfIcon from "../../assets/types/pdf.png";
import msOfficeIcon from "../../assets/types/ms-office.png";
import jpgIcon from "../../assets/types/jpg.png";
import zipIcon from "../../assets/types/zip.png";
import xlsxIcon from "../../assets/types/xlsx.png";
import defaultIcon from "../../assets/types/default.png";
import useApi from "../../utils/useApi";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import clsx from "clsx";

const FormList = ({handleShowHeader, handleShowSettingsHeader}) => {
    const [loading, setLoading] = useState(true);
    const [forms, setForms] = useState(null);
    const { getForms } = useApi();

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initializeForms();
    }, []);

    const initializeForms = async () => {
        const response = await getForms();
        setForms(response.documents);
        setLoading(false);
    }

    const getFileIcon = (category) => {
        switch (category?.toLowerCase()) {
            case 'png':
                return pngIcon;
            case 'pdf':
                return pdfIcon;
            case 'docx':
                return msOfficeIcon;
            case 'jpg':
            case 'jpeg':
                return jpgIcon;
            case 'zip':
                return zipIcon;
            case 'xlsx':
                return xlsxIcon;
            default:
                return defaultIcon;
        }
    };

    if(loading){
        return <LoadingScreen />
    }

    if (!forms || forms.length === 0) {
        return (
            <div className="flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg z-10 page-container">
                <AlertCircle className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2"/>
                <span className="text-gray-500 dark:text-gray-400">No forms available</span>
            </div>
        );
    }

    return (
        <div className="w-full z-10 relative md:p-16 p-6 bg-white dark:bg-gray-900 page-container">
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className={clsx(
                        'text-3xl font-bold dark:text-white text-gray-900',
                    )}>
                        Worksheets
                    </h1>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {forms.map((form) => (
                    <div
                        key={form.id}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/10 p-4
                                 transition-all hover:shadow-lg dark:hover:shadow-gray-900/20
                                 border border-gray-100 dark:border-gray-700"
                    >
                        {/* Form Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={getFileIcon(form.category)}
                                alt={`${form.category} file`}
                                className="w-12 h-12 object-contain"
                            />
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm text-gray-900 dark:text-gray-100 mt-1 truncate font-medium"
                                    title={form.file_name}>
                                    {form.file_name}
                                </h3>
                            </div>
                        </div>

                        {/* Form Comments */}
                        {form.document_comments && (
                            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-md mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {form.document_comments}
                                </p>
                            </div>
                        )}

                        {/* Download Button */}
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                            <a
                                target={"_blank"}
                                href={form.url}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                                         text-sm font-medium text-blue-600 dark:text-blue-400
                                         bg-blue-50 dark:bg-blue-900/20 rounded-md
                                         hover:bg-blue-100 dark:hover:bg-blue-900/30
                                         transition-colors"
                                title="Download Form"
                            >
                                <Download className="w-4 h-4"/>
                                Download Form
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FormList;
